import Swal from "sweetalert2";
import { toast } from "react-toastify";
import moment from "moment";
import CurrencyCode from "currency-codes";
import { isEmpty } from "lodash";
import { doLogout } from "./functions";
import { saveAs } from "file-saver";

const gateway = {
	tctp: "2C2P",
	uqpay: "UQPay",
	ipay88: "iPay88",
};

// Get merchant's payment gateway MDR
const getMerchantMdr = (merchant, paymentGateway) => {
	if (paymentGateway === "tctp" && !isEmpty(merchant.mdrTctp))
		return merchant.mdrTctp;
	else if (paymentGateway === "ipay88" && !isEmpty(merchant.mdrIpay88))
		return merchant.mdrIpay88;
	else if (paymentGateway === "uqpay" && !isEmpty(merchant.mdrUqpay))
		return merchant.mdrUqpay;
	else if (paymentGateway === "rms" && !isEmpty(merchant.mdrRms))
		return merchant.mdrRms;
	else if (paymentGateway === "ghlth" && !isEmpty(merchant.mdrGhlth))
		return merchant.mdrGhlth;
	else if (paymentGateway === "netzme" && !isEmpty(merchant.mdrNetzme))
		return merchant.mdrNetzme;
	if (!isEmpty(merchant.mdrv2)) return merchant.mdrv2;
};

// Get merchant's MDR based on transaction type
const getTransactionMdr = ({
	foundMdr,
	currency,
	paymentType,
	merchant,
	paymentGateway,
}) => {
	// Find MDR for currency
	const mdrList = foundMdr[currency];
	if (!mdrList) throw new Error(`Rate for currency ${currency} is not set`);

	// Find MDR for payment type
	const rateFound = mdrList.find((rate) => rate.type.includes(paymentType));
	if (!rateFound)
		throw new Error(
			`Rate for payment type ${paymentType} is not set for ${merchant.name} ${paymentGateway}`
		);

	return rateFound;
};

const Util = {
	gateway,
	expireSession: () => {
		return Swal.fire({
			icon: "error",
			title: "Session Expired",
			text: "Please login again!",
			onOpen: async () => {
				await doLogout();
			},
		});
	},
	promptConfirmation: () => {
		return Swal.fire({
			title: "Are you sure?",
			icon: "warning",
			showCancelButton: true,
			confirmButtonColor: "#EC8F4D",
			cancelButtonColor: "#D31C15",
		});
	},
	filterBlank: (obj) => {
		return Object.entries(obj).reduce(
			(a, [k, v]) => (v ? { ...a, [k]: v } : a),
			{}
		);
	},
	difference: (arrayA, arrayB) => {
		return arrayA.filter((x) => !arrayB.includes(x));
	},
	showModal: (id) => {
		return UIkit.modal(`#${id}`).show();
	},
	hideModal: (id) => {
		return UIkit.modal(`#${id}`).hide();
	},
	sortObject: (order, targetObject) => {
		let sorted = {};
		for (let key of order) {
			if (targetObject[key]) sorted[key] = targetObject[key];
		}
		return sorted;
	},
	toDateTimeString: (date, resetMinSec = false) => {
		let temp = moment(date);
		if (resetMinSec) temp = temp.minute(0).second(0);
		return temp.format("DD/MM/YYYY HH:mm");
	},
	translateGateway: (key) => {
		return gateway[key] || key;
	},
	copyToClipboard: (text) => {
		const textField = document.createElement("textarea");
		textField.innerText = text;
		document.body.appendChild(textField);
		textField.select();
		document.execCommand("copy");
		textField.remove();
		toast.info("Copied!");
	},
	downloadFile: (content, filename) => {
		const element = document.createElement("a");
		const file = new Blob([content], { type: "text/plain" });
		element.href = URL.createObjectURL(file);
		element.download = filename;
		document.body.appendChild(element); // Required for this to work in FireFox
		element.click();
	},
	downloadReport: (content, filename) => {
		const file = new Blob([content], {
			type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		});
		saveAs(file, filename);
	},

	noRatesPayment: ["applepay", "googlepay", "ipp"],
	currencyNumToCode: (num) => CurrencyCode.number(num).code,

	capitalizeFirstLetter: (string) => {
		return string.charAt(0).toUpperCase() + string.slice(1);
	},

	// Get isLocal based on settled MDR (2C2P only)
	isMdrLocal: ({
		isLocal,
		paymentGateway,
		merchant,
		currency,
		paymentType,
		merchantMdr,
	}) => {
		// Calculate for 2C2P and UQPay
		const foundMdr = getMerchantMdr(merchant, paymentGateway);
		if (!foundMdr)
			throw new Error(`Merchant does not have MDR set for ${paymentGateway}`);

		const { transactionFee } = getTransactionMdr({
			foundMdr,
			currency,
			paymentType,
			merchant,
			paymentGateway,
		});

		// Get percentage & fixed fees
		if (transactionFee) {
			if (transactionFee.local === transactionFee.overseas && isLocal !== null)
				return isLocal;
			if (transactionFee.local === merchantMdr) return true;
			if (transactionFee.overseas === merchantMdr) return false;
		}

		return true;
	},
};

export default Util;
